/**
 * What "Restore versions" restores each file to — the ONE implementation behind
 * both the modal's read (`GET …/message/:id/versions`) and the write
 * (`POST …/restore-versions`), so what the modal shows is what gets restored
 * (#768).
 *
 * ── The rule ─────────────────────────────────────────────────────────────
 *
 * The file version store is the only restoration source. The session DB no
 * longer keeps a file's text (patch parts carry version ids), so there is no
 * "restore from the content in the conversation" fallback any more.
 *
 *  • the EXACT version the snapshot recorded, when the file still keeps it —
 *    by id, or by its number (unique per file, and the only thing a Bin
 *    round-trip keeps: it re-mints every version id);
 *  • otherwise — only when a version WAS recorded and has since been pruned —
 *    a substitute, flagged `pruned` so the modal and the result say so:
 *      `before`: the newest kept version that still PREDATES the AI's change
 *                (the closest at or below the requested number when one
 *                survives, else the oldest kept one below the change). If the
 *                pruning reached past the AI's change, there is NO such
 *                version: restoring "the oldest kept" would put back a state
 *                that still contains the change and report it as undone, so
 *                the row is not restorable instead (review 2026-09-23);
 *      `after`:  the oldest kept version AT OR AFTER the requested one (or,
 *                when its number is unknown, at or after the NUMBER of the AI's
 *                first write). If every kept version is older — pinned history
 *                in a multi-write turn — the newest, only when it is verifiably
 *                at or past the change (never one that could predate it, which
 *                would undo it). With no number to bound by, not guessed. In a
 *                single-write turn this means a pruned "after" is refused;
 *  • no substitute without an anchor: a version never RECORDED, or a pruned
 *    one with no number and no change boundary to check against, is reported
 *    not restorable (`not-recorded` / `no-boundary`) rather than guessed —
 *    an unchecked pick could land before the change or far past it (review
 *    2026-09-23);
 *  • a file that no longer exists but is in the Bin (the AI deleted it, or
 *    someone did since) is restored FROM the Bin by the same rule, matched by
 *    version number — see `planFromBin`;
 *  • never a silent no-op: a file that no longer exists (and is not in the
 *    Bin), keeps no version, or has no usable version is reported as not
 *    restorable, with a reason.
 *
 * Deliberately no agent exemption from pruning (`selectPrunableVersions`) —
 * this module degrades instead.
 */

export type RestoreMode = 'before' | 'after'
export type SnapshotAction = 'created' | 'updated' | 'deleted'

/** One file's net change over the restore scope, as read from patch parts. */
export interface RestoreSnapshotRef {
  path: string
  action: SnapshotAction
  fileId?: string
  /** The version the file was at BEFORE the scope (first snapshot's). */
  beforeVersionId?: string
  beforeVersionNumber?: number
  /** The version the file was at AFTER the scope (last snapshot's). */
  afterVersionId?: string
  afterVersionNumber?: number
  /** The FIRST version the AI wrote in scope (first snapshot's "after") — the
   *  boundary a `before` substitute must stay below, or it still contains the
   *  change. Not `afterVersion*`, which is the LAST one across the scope. */
  changeVersionId?: string
  changeVersionNumber?: number
  /** The path names a different file now than before the change (deleted and
   *  recreated in scope): `before` cannot be restored from the new file. */
  replaced?: boolean
}

/** The fields of a message this module reads — `Session.messagesAll` rows fit. */
export interface MessageLike {
  info: { role: string }
  parts: ReadonlyArray<{ type: string; snapshots?: unknown }>
}

interface RawSnapshot {
  path?: unknown
  action?: unknown
  fileId?: unknown
  beforeVersionId?: unknown
  beforeVersionNumber?: unknown
  afterVersionId?: unknown
  afterVersionNumber?: unknown
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)

/**
 * The files a restore covers, one entry per path.
 *
 * Scope: `after` = this message's own turn (up to the next user message);
 * `before` = this turn AND every later one — "the state before this message"
 * has to undo later turns' changes to the same files too. (The two routes used
 * to disagree here: the modal listed every later turn's files in `before` mode
 * while the restore only looked at the first turn, so a later-turn row was
 * silently skipped.)
 *
 * A path touched several times in scope is merged: the version BEFORE comes
 * from its first snapshot, the version AFTER from its last, and the action is
 * the NET change — did the file exist before the scope, and after it? A file
 * created and then deleted inside the scope changed nothing and is dropped.
 */
export function collectRestoreSnapshots(msgs: readonly MessageLike[], userIdx: number, mode: RestoreMode): RestoreSnapshotRef[] {
  const merged = new Map<string, RestoreSnapshotRef & { existedBefore: boolean; existsAfter: boolean }>()
  // #1062 step C: a file MOVED in scope (a bash `mv` kept its id, or move_file) has its entries under several paths
  // with ONE id: merged by that id (else two version restores run on one id and the second wins with an in-between
  // version), its path the latest. And a path it moved ONTO keeps its own entry — `rm b; mv a b` is two files: `a`
  // moved, the old `b` deleted (review: a path-keyed merge made them one "replaced" file).
  const movedIds = new Set(collectRestoreMoves(msgs, userIdx, 'before').map((m) => m.fileId))
  for (let i = userIdx + 1; i < msgs.length; i++) {
    if (mode === 'after' && msgs[i].info.role === 'user') break
    for (const part of msgs[i].parts) {
      if (part.type !== 'patch' || !Array.isArray(part.snapshots)) continue
      for (const raw of part.snapshots as RawSnapshot[]) {
        const path = str(raw?.path)
        const action = raw?.action
        if (!path || (action !== 'created' && action !== 'updated' && action !== 'deleted')) continue
        const rawId = str(raw.fileId)
        const key = rawId && movedIds.has(rawId) ? `\u0000id:${rawId}` : path
        const existing = merged.get(key)
        if (existing) existing.path = path
        if (!existing) {
          merged.set(key, {
            path,
            action,
            fileId: str(raw.fileId),
            beforeVersionId: str(raw.beforeVersionId),
            beforeVersionNumber: num(raw.beforeVersionNumber),
            afterVersionId: str(raw.afterVersionId),
            afterVersionNumber: num(raw.afterVersionNumber),
            changeVersionId: str(raw.afterVersionId),
            changeVersionNumber: num(raw.afterVersionNumber),
            existedBefore: action !== 'created',
            existsAfter: action !== 'deleted',
          })
        } else {
          const rawFileId = str(raw.fileId)
          if (rawFileId && existing.fileId && rawFileId !== existing.fileId) {
            // The path now names a DIFFERENT file (deleted and recreated in
            // scope). Restore acts on the file as it exists now, so follow the
            // new id — the old one is binned, and keeping it reported "already
            // deleted" while the live file stayed (review 2026-09-23). The
            // before-versions belonged to the old file and mean nothing for the
            // new one, so they are dropped rather than looked up against it.
            existing.fileId = rawFileId
            existing.replaced = true
            existing.beforeVersionId = undefined
            existing.beforeVersionNumber = undefined
            existing.changeVersionId = str(raw.afterVersionId)
            existing.changeVersionNumber = num(raw.afterVersionNumber)
          } else {
            existing.fileId = existing.fileId ?? rawFileId
          }
          existing.afterVersionId = str(raw.afterVersionId)
          existing.afterVersionNumber = num(raw.afterVersionNumber)
          existing.existsAfter = action !== 'deleted'
        }
      }
    }
  }
  const out: RestoreSnapshotRef[] = []
  for (const { existedBefore, existsAfter, ...snap } of merged.values()) {
    if (!existedBefore && !existsAfter) continue
    snap.action = !existedBefore ? 'created' : !existsAfter ? 'deleted' : 'updated'
    out.push(snap)
  }
  return out
}

/** #1062 step C — one item MOVED in scope, by id: where it was before the scope (its first move's origin), and where
 *  it is after (its last move's destination). */
export interface RestoreMoveRef {
  fileId: string
  /** The path before the scope (labels only; the move back is by id). */
  fromPath: string
  fromParentId: string | null
  fromName: string
  /** The path after the scope. */
  toPath: string
}

interface RawMove {
  action?: unknown
  fileId?: unknown
  path?: unknown
  fromPath?: unknown
  fromParentId?: unknown
  fromName?: unknown
}

/**
 * #1062 step C — the moves a restore undoes: the `moved` entries of the patch parts in scope, merged by id (the first
 * origin, the last destination). Only `before` moves anything back — `after` is the state right after this message's
 * own turn, where its moves stand. An item moved away and back to the same place changed nothing and is dropped.
 */
export function collectRestoreMoves(msgs: readonly MessageLike[], userIdx: number, mode: RestoreMode): RestoreMoveRef[] {
  if (mode !== 'before') return []
  const byId = new Map<string, RestoreMoveRef>()
  for (let i = userIdx + 1; i < msgs.length; i++) {
    for (const part of msgs[i].parts) {
      if (part.type !== 'patch' || !Array.isArray(part.snapshots)) continue
      for (const raw of part.snapshots as RawMove[]) {
        if (raw?.action !== 'moved') continue
        const fileId = str(raw.fileId)
        const toPath = str(raw.path)
        const fromPath = str(raw.fromPath)
        const fromName = str(raw.fromName)
        if (!fileId || !toPath || !fromPath || !fromName) continue
        const fromParentId = typeof raw.fromParentId === 'string' ? raw.fromParentId : null
        const prev = byId.get(fileId)
        if (prev) prev.toPath = toPath
        else byId.set(fileId, { fileId, fromPath, fromParentId, fromName, toPath })
      }
    }
  }
  return [...byId.values()].filter((m) => m.fromPath !== m.toPath)
}

/** A kept version, as `storage.listVersions` returns it (NEWEST FIRST). */
export interface KeptVersion {
  id: string
  versionNumber?: number | null
  createdAt: Date
  source: string
  description?: string | null
  /** Its content — lets a restore say it changed nothing (the file already
   *  holds it); on a Bin version, what a restore verifies before the un-bin (#1397). */
  contentHash?: string | null
}

export interface RestoreTarget {
  version: KeptVersion
  /** The version's display number (`vN`). */
  number: number
  /** The recorded version was pruned; this is the substitute. (A version that
   *  was never recorded is never substituted — see the header.) */
  pruned: boolean
}

/** `vN` for the version at `idx` of a newest-first list. Rows written before
 *  version numbers existed fall back to their position (oldest = v1). */
export function versionNumberAt(versions: readonly KeptVersion[], idx: number): number {
  return versions[idx].versionNumber ?? versions.length - idx
}

/**
 * Why a file that still exists has no version to restore.
 *
 *  • `no-versions`        — it keeps no version at all;
 *  • `not-recorded`       — the change recorded no version to go back to
 *                           (rows from before version tracking);
 *  • `no-boundary`        — the recorded version was pruned and nothing was
 *                           recorded to tell which kept version still predates
 *                           (or, for `after`, follows) the change — any pick
 *                           would be a guess;
 *  • `pruned-past-change` — `before` only: retention pruned everything older
 *                           than the AI's change, so every kept version still
 *                           contains it. Restoring one would report the change
 *                           as undone while leaving it in place;
 *  • `file-replaced`      — `before` only: the path now names a different file
 *                           than it did before the change (deleted and
 *                           recreated in scope); the old file is in the Bin;
 *  • `bin-folder-gone`    — the file is in the Bin, but the folder it was
 *                           deleted from is gone too: where it goes back is the
 *                           user's call, in the Bin itself;
 *  • `no-access`          — the caller may not change the file (or, from the
 *                           Bin, the folder it goes back into) — decided by
 *                           the routes, not by `planRestore`, which is pure.
 */
export type UnavailableReason = 'no-versions' | 'not-recorded' | 'no-boundary' | 'pruned-past-change' | 'file-replaced' | 'bin-folder-gone' | 'no-access'

/** The boundary of the AI's change, for a `before` substitute. */
export interface ChangeBoundary {
  /** Number of the first version the AI wrote in scope. */
  number?: number
  /** Its id — used when numbers were not recorded (older rows). */
  id?: string
}

/**
 * The version to restore: the exact one when kept, else the substitute
 * described at the top of this file, else why there is none.
 *
 * `versions` is newest-first — `storage.listVersions`' order.
 *
 * Substitutes compare RECORDED version numbers only. `versionNumberAt`'s
 * positional fallback (rows written before numbers existed) is a display aid in
 * a different numbering scheme; comparing it with a real number could class a
 * version written AFTER the change as "before" it (review 2026-09-23).
 */
export function pickRestoreTarget(
  versions: readonly KeptVersion[],
  requestedId: string | undefined,
  requestedNumber: number | undefined,
  mode: RestoreMode,
  change: ChangeBoundary = {},
): RestoreTarget | { unavailable: UnavailableReason } {
  if (versions.length === 0) return { unavailable: 'no-versions' }
  if (!requestedId && requestedNumber === undefined) return { unavailable: 'not-recorded' }
  // Exact: the recorded id, else the recorded NUMBER — a file's version numbers
  // are unique and survive a Bin round-trip, which re-mints every id (so after
  // any delete → restore the id alone would call the very same version "pruned").
  let exact = requestedId ? versions.findIndex((v) => v.id === requestedId) : -1
  if (exact < 0 && requestedNumber !== undefined) exact = versions.findIndex((v) => v.versionNumber === requestedNumber)
  if (exact >= 0) return { version: versions[exact], number: versionNumberAt(versions, exact), pruned: false }

  const at = (idx: number): RestoreTarget => ({ version: versions[idx], number: versionNumberAt(versions, idx), pruned: true })
  // Where the change version (the AI's first write) sits in the kept list, if
  // it is kept — ONE lookup, shared by both modes' boundary checks.
  const changePos = change.id ? versions.findIndex((v) => v.id === change.id) : -1
  const recorded = (idx: number): number | undefined => versions[idx].versionNumber ?? undefined
  const indices = versions.map((_, i) => i) // newest-first positions

  if (mode === 'after') {
    // The bound an "after" substitute must reach: the requested version, else
    // the first version the AI wrote (the after-state is at or past it). With
    // neither, the oldest kept version could be a PRE-change pinned one —
    // restoring it would undo the change while claiming the opposite.
    const bound = requestedNumber ?? change.number
    if (bound === undefined) return { unavailable: 'no-boundary' }
    // Oldest kept version at or after the bound.
    for (let i = versions.length - 1; i >= 0; i--) {
      const n = recorded(i)
      if (n !== undefined && n >= bound) return at(i)
    }
    // None at or after it: every numbered kept version is older (pinned). The
    // newest is the nearest — but only if it is VERIFIABLY at or past the AI's
    // change (by number, or by the change version's kept position); otherwise
    // it may predate the change, and restoring it would UNDO what "after" keeps.
    const newest = recorded(0)
    const newestPastChange = change.number !== undefined
      ? newest !== undefined && newest >= change.number
      : changePos >= 0
    if (newestPastChange) return at(0)
    return { unavailable: 'no-boundary' }
  }

  // `before`: only versions that PREDATE the AI's change qualify.
  let below: number[] | null = null
  if (change.number !== undefined) {
    below = indices.filter((i) => { const n = recorded(i); return n !== undefined && n < change.number! })
  } else if (changePos >= 0) {
    // The change's own version is kept: it is the boundary by position.
    below = indices.filter((i) => i > changePos)
  }
  if (below === null) {
    if (requestedNumber !== undefined) {
      // The requested BEFORE version predates the change by definition, so
      // anything numbered at or below it does too — including pinned history
      // older than a pruned change version (review 2026-09-23).
      below = indices.filter((i) => { const n = recorded(i); return n !== undefined && n <= requestedNumber })
    } else if (change.id) {
      // The change version itself was pruned and nothing is numbered:
      // retention prunes oldest-first, so what is kept is newer than it.
      return { unavailable: 'pruned-past-change' }
    } else {
      // Nothing to check a substitute against: an unchecked "oldest kept" is
      // exactly the version that may still contain the change.
      return { unavailable: 'no-boundary' }
    }
  }
  if (below.length === 0) return { unavailable: 'pruned-past-change' }
  if (requestedNumber !== undefined) {
    // The closest surviving version at or below the one asked for (a pinned
    // one, typically) — newest-first, so the first match is the newest.
    const atOrBelow = below.find((i) => { const n = recorded(i); return n !== undefined && n <= requestedNumber })
    if (atOrBelow !== undefined) return at(atOrBelow)
  }
  return at(below[below.length - 1]) // oldest kept that still predates the change
}

export type RestorePlan =
  /** The target state is "no file": delete it (a no-op if already gone). */
  | { kind: 'delete' }
  /** Restore this version (exact or substitute). */
  | { kind: 'version'; target: RestoreTarget; requestedNumber: number | null }
  /** The file is in the Bin: restore it to where it was deleted from, then —
   *  unless `target` is the version it was binned at — to `target`. */
  | { kind: 'bin'; binId: string; target: RestoreTarget; requestedNumber: number | null }
  /** The file no longer exists and is not in the Bin as a file of its own
   *  (purged, or binned inside a folder), so there is nothing to restore. */
  | { kind: 'missing' }
  /** The file has no usable version — see `UnavailableReason`. */
  | { kind: 'unavailable'; reason: UnavailableReason }

/** Words for an `unavailable` row / failure — ONE place, so the modal and the
 *  route's `failed[].reason` agree. */
export const UNAVAILABLE_COPY: Record<UnavailableReason, string> = {
  'no-versions': 'no kept version',
  'not-recorded': 'no version was recorded for this change',
  'no-boundary': 'the recorded version was pruned, and no kept version can be confirmed to match it',
  'pruned-past-change': 'the version before this change was pruned',
  'file-replaced': 'the file was replaced after this change — the earlier one is in the Bin',
  'bin-folder-gone': 'it is in the Bin, but the folder it was in is gone — restore it from the Bin',
  'no-access': 'you do not have permission to change this file',
}

/** Whether the restore's target state is "no file" (created → before,
 *  deleted → after). Such a row never needs the file's version list, so the
 *  route asks this first and skips that query. */
export function restoreTargetIsAbsent(snap: Pick<RestoreSnapshotRef, 'action'>, mode: RestoreMode): boolean {
  return (snap.action === 'created' && mode === 'before') || (snap.action === 'deleted' && mode === 'after')
}

/**
 * A file that is in the Bin as an item of its own, as a restore sees it.
 *
 * A bin restore re-creates the file's versions under NEW ids — only their
 * numbers survive — so a snapshot's recorded version ids can never match, and
 * a binned file is planned by NUMBER. `versions` are the Bin's versions as
 * `KeptVersion`s (see `binKeptVersions`), newest first.
 */
export interface BinnedFile {
  binId: string
  versions: readonly KeptVersion[]
  /** The folder it was deleted from is gone, so it has nowhere to go back to. */
  folderGone: boolean
}

/** A bin snapshot's version history as `KeptVersion`s, newest first. A version
 *  with no number is left out: nothing recorded can name it. */
export function binKeptVersions(
  versions: ReadonlyArray<{ versionNumber: number | null; createdAt: string; source: string; description?: string | null; contentHash?: string | null }>,
): KeptVersion[] {
  return versions
    .filter((v): v is typeof v & { versionNumber: number } => typeof v.versionNumber === 'number')
    // A stand-in id — never a real version id; a Bin version is named by number.
    .map((v) => ({ id: `bin:v${v.versionNumber}`, versionNumber: v.versionNumber, createdAt: new Date(v.createdAt), source: v.source, description: v.description ?? null, ...(v.contentHash ? { contentHash: v.contentHash } : {}) }))
    .sort((a, b) => b.versionNumber - a.versionNumber)
}

/**
 * Decide what restoring one file does. `fileExists` / `versions` describe the
 * file as it is NOW (`storage.getFile(fileId)` / `storage.listVersions(fileId)`);
 * `binned` is its Bin item when it does not exist.
 */
export function planRestore(
  snap: RestoreSnapshotRef,
  mode: RestoreMode,
  fileExists: boolean,
  versions: readonly KeptVersion[],
  binned: BinnedFile | null = null,
): RestorePlan {
  if (restoreTargetIsAbsent(snap, mode)) return { kind: 'delete' }
  if (mode === 'before' && snap.replaced) return { kind: 'unavailable', reason: 'file-replaced' }
  if (!fileExists) return binned ? planFromBin(snap, mode, binned) : { kind: 'missing' }
  const requestedId = mode === 'before' ? snap.beforeVersionId : snap.afterVersionId
  const requestedNumber = mode === 'before' ? snap.beforeVersionNumber : snap.afterVersionNumber
  const picked = pickRestoreTarget(versions, requestedId, requestedNumber, mode, {
    number: snap.changeVersionNumber,
    id: snap.changeVersionId,
  })
  if ('unavailable' in picked) return { kind: 'unavailable', reason: picked.unavailable }
  return { kind: 'version', target: picked, requestedNumber: picked.pruned ? requestedNumber ?? null : picked.number }
}

/**
 * The `bin` plan: the same exact-else-substitute rule as a live file, by
 * version NUMBER (a binned file's ids are not the ones the snapshot recorded).
 *
 * The worker records the version a deleted file was binned at, so this is
 * normally an exact match. Only a row with NO recorded number (an older row,
 * a failed lookup) whose sole change in scope was the delete falls back to the
 * Bin's newest version — the file as it was when deleted. A recorded number is
 * never overridden by that fallback: an edit and a delete in one turn record
 * the pre-edit number, and the Bin's newest is then the AI's edit.
 */
function planFromBin(snap: RestoreSnapshotRef, mode: RestoreMode, binned: BinnedFile): RestorePlan {
  if (binned.folderGone) return { kind: 'unavailable', reason: 'bin-folder-gone' }
  const { versions } = binned
  if (versions.length === 0) return { kind: 'unavailable', reason: 'no-versions' }
  const requestedNumber = mode === 'before' ? snap.beforeVersionNumber : snap.afterVersionNumber
  // Only with NO recorded number: a delete records no version the AI wrote,
  // so "no change boundary" does not prove the AI only deleted it — an edit
  // then a delete in ONE turn leaves the same shape, and the Bin's newest is
  // then the AI's edit (review 2026-09-23). A recorded number is matched.
  const aiWroteInScope = snap.changeVersionNumber !== undefined || snap.changeVersionId !== undefined
  if (mode === 'before' && snap.action === 'deleted' && requestedNumber === undefined && !aiWroteInScope) {
    const target: RestoreTarget = { version: versions[0], number: versionNumberAt(versions, 0), pruned: false }
    return { kind: 'bin', binId: binned.binId, target, requestedNumber: target.number }
  }
  // By number: the recorded ids were re-minted, so neither can match.
  const picked = pickRestoreTarget(versions, undefined, requestedNumber, mode, { number: snap.changeVersionNumber })
  if ('unavailable' in picked) return { kind: 'unavailable', reason: picked.unavailable }
  return { kind: 'bin', binId: binned.binId, target: picked, requestedNumber: picked.pruned ? requestedNumber ?? null : picked.number }
}
